import type { UseRealtimeConnectionStatus } from 'inngest/react';
import { useEffect, useRef } from 'react';
import { useAiChatStore } from '../store/aiChat.store';

const CONNECTION_STATUS_GROUP_ID = 'connection-status';

/**
 * Hook that manages the connection status in the unified status system.
 * Shows connecting/reconnecting/disconnected/error states as status toasts.
 */
export function useConnectionStatus() {
  const realtimeState = useAiChatStore((state) => state.realtimeState);
  const upsertStatus = useAiChatStore((state) => state.upsertStatus);
  const completeStatus = useAiChatStore((state) => state.completeStatus);
  const removeStatus = useAiChatStore((state) => state.removeStatus);
  const previousStateRef = useRef<UseRealtimeConnectionStatus>('closed');

  useEffect(() => {
    const previousState = previousStateRef.current;
    previousStateRef.current = realtimeState;

    switch (realtimeState) {
      case 'connecting':
        upsertStatus(CONNECTION_STATUS_GROUP_ID, { status: 'pending', message: 'Connecting...', variant: 'info' });
        break;

      case 'paused':
        upsertStatus(CONNECTION_STATUS_GROUP_ID, { status: 'pending', message: 'Reconnecting...', variant: 'warning' });
        break;

      case 'open':
        // Only show "Connected" if transitioning from connecting/reconnecting
        if (previousState === 'connecting' || previousState === 'paused') {
          upsertStatus(CONNECTION_STATUS_GROUP_ID, { status: 'pending', message: 'Connected', variant: 'success' });
          // Mark as complete so it auto-dismisses
          completeStatus(CONNECTION_STATUS_GROUP_ID);
        } else {
          // Already connected, just remove any existing status
          removeStatus(CONNECTION_STATUS_GROUP_ID);
        }
        break;

      case 'error':
        upsertStatus(CONNECTION_STATUS_GROUP_ID, { status: 'pending', message: 'Connection Error', variant: 'error' });
        break;

      case 'closed':
        // Only show disconnected if we were previously connected
        if (previousState === 'open') {
          upsertStatus(CONNECTION_STATUS_GROUP_ID, { status: 'pending', message: 'Disconnected', variant: 'error' });
        }
        break;
    }
  }, [realtimeState, upsertStatus, completeStatus, removeStatus]);
}

// Keep default export for backwards compatibility, but it's now a no-op
export default function AiChatStatus() {
  useConnectionStatus();
  return null;
}
